import { Avatar, Stack, Typography } from "@mui/material"
import { IOmniaShell, SharedFieldsService } from "@omnia/types"
import { createElement, Fragment, useCallback, useEffect, useState } from "react"
import { defineReactComponent, ReactAngularComponentContext } from "../react/angular"
import { Button, ButtonBadge } from "../react/buttons/Button"
import { showDialog } from "../react/dialog"
import { DialogBody } from "../react/DialogBody"
import { DangerButton, DialogFooter, DialogFooterButtons, SaveButton } from "../react/DialogFooter"
import { DialogHeader } from "../react/DialogHeader"
import { Msg } from "../react/Msg"
import { showDeleteCancel } from "../react/msgbox"
import { TermsAndConditionsDialog, TermsAndConditionsDialogProps } from "../react/TermsAndConditionsDialog"
import { showManagePartnerCroppingsDialog } from "./ManagePartnerCroppingsDialog"
import { ManagePartnersProvider, useManagePartnersContext } from "./ManagePartnersContext"
import {
    ManagePartnersDialogDependencies,
    PartnerConnectionDto,
    PartnerConnectionsDataDto,
    PartnerDto,
    RequiredDocumentDto,
    SignatureDto,
} from "./types"

/**
 * Defines the `ManagePartnersDialog` React component to integrate with Angular
 */
export function defineManagePartnersDialogReact() {
    return defineReactComponent({
        inject: {
            shell: "OmniaShell",
            fieldSvc: "OmniaFieldsService",
        },

        start(context: ReactAngularComponentContext<{ shell: IOmniaShell; fieldSvc: SharedFieldsService }>) {
            const { rootElement, dependencies } = context

            const shell: IOmniaShell = dependencies["shell"]
            const session = shell.getSession()

            const element = createElement(ManagePartnersDialog, {
                ...context,
                dependencies: {
                    ...dependencies,
                    session,
                },
            })

            ReactDOM.render(element, rootElement)
        },
    })
}

type ManagePartnersDialogProps = ReactAngularComponentContext<ManagePartnersDialogDependencies>

/**
 * Manage partners for the current holding.
 * @param dependencies: the dependencies injected by Angular
 * @param close: the function to close the dialog
 * @returns React component
 */
function ManagePartnersDialog({ dependencies: { shell, fieldSvc, session }, close }: ManagePartnersDialogProps) {
    const currentYearId = fieldSvc.yearId

    const [state, setState] = useState<PartnerConnectionsDataDto>(null)

    const refreshCurrentYear = useCallback(() => {
        return session.http
            .get<PartnerConnectionsDataDto>(`/partners/year/${currentYearId}/current`, false)
            .then((data) => setState(data.data))
            .catch(() => setState(null))
    }, [session, currentYearId])

    useEffect(() => {
        refreshCurrentYear()
    }, [currentYearId])

    const handleCroppingsChange = useCallback(
        async (partnerId: number, enabledCroppingIds: number[]) => {
            await session.http.post(
                `/partners/update`,
                {
                    partnerId: partnerId,
                    yearId: currentYearId,
                    // Enable the partner itself.
                    enabled: true,
                    // The enabled croppings, for this partner.
                    croppings: Object.fromEntries(enabledCroppingIds.map((x) => [x, "enabled"])),
                },
                { blockUi: false }
            )
            refreshCurrentYear()
        },
        [currentYearId]
    )

    return (
        <ManagePartnersProvider fieldSvc={fieldSvc} session={session} shell={shell}>
            <div className="omnia-manage-partners-dialog">
                <DialogHeader icon="fas fa-webhook">
                    <Msg>Partners</Msg>
                </DialogHeader>
                <DialogBody>
                    <div className="omnia-manage-partners-dialog--intro">
                        <p>
                            Enabling a partner will allow them to access information about your holding and the
                            croppings you select.
                        </p>
                    </div>
                    {state == null || state.partners.length === 0 ? (
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minWidth: 200,
                                minHeight: 200,
                            }}
                        >
                            {state === null ? (
                                <p>Loading partners...</p>
                            ) : (
                                <p>There are no partners available for this holding.</p>
                            )}
                        </div>
                    ) : (
                        <>
                            {state.partners.map((x) => (
                                <Fragment key={x.partner.id}>
                                    <PartnerSection
                                        key={x.partner.id}
                                        partner={x}
                                        requiredDocuments={state.requiredDocuments[x.partner.id]}
                                        signatures={state.signatures}
                                        onChangeEnabled={refreshCurrentYear}
                                        onChangeEnabledCroppingIds={(value) =>
                                            handleCroppingsChange(x.partner.id, value)
                                        }
                                    />
                                </Fragment>
                            ))}
                        </>
                    )}
                </DialogBody>
                <DialogFooter>
                    <DialogFooterButtons onClose={close} />
                </DialogFooter>
            </div>
        </ManagePartnersProvider>
    )
}

type PartnerSectionProps = {
    partner: PartnerConnectionDto
    requiredDocuments: RequiredDocumentDto[]
    signatures: SignatureDto[]
    onChangeEnabled: (value: boolean) => void
    onChangeEnabledCroppingIds: (value: number[]) => void
    loading?: boolean
    disabled?: boolean
}

export function PartnerSection({
    partner: { partner, croppingIds, enabled },
    requiredDocuments,
    signatures,
    onChangeEnabled,
    onChangeEnabledCroppingIds,
    disabled,
}: PartnerSectionProps) {
    const { id, name, description } = partner
    const { shell, session, fieldSvc } = useManagePartnersContext()
    const yearId = fieldSvc.yearId

    const showConditions = useTermsAndConditionsDialog()

    const [isLoading, setLoading] = useState<boolean>(false)

    const requireAcceptance = useCallback(async () => {
        for (let index = 0; index < requiredDocuments.length; index++) {
            const requiredDocument = requiredDocuments[index]
            const signature = signatures.find(
                (x) => x.legalDocumentVersionId == requiredDocument.legalDocumentVersionId
            )
            if (
                signature ||
                (await showConditions({
                    contentHtml: requiredDocument.text,
                    checkboxLabel:
                        "I have read and agree to the Terms & Conditions and agree to sharing farm data with the partner.",
                    acceptedBy: signature
                        ? {
                              name: [signature.firstName, signature.lastName].filter((s) => !!s).join(" "),
                              timestamp: signature.createdAt,
                          }
                        : null,
                }))
            ) {
                await session.http.post(`/partners/legal-documents/sign`, {
                    ids: [requiredDocument.legalDocumentVersionId],
                })
            } else {
                return false
            }
        }

        return true
    }, [session, name, requiredDocuments])

    const setEnabled = useCallback(
        async (value: boolean) => {
            if (!value || (await requireAcceptance())) {
                setLoading(true)
                await session.http.post(
                    `/partners/update`,
                    {
                        partnerId: id,
                        yearId: yearId,
                        // Enable the partner itself.
                        enabled: value,
                    },
                    { blockUi: false }
                )
                onChangeEnabled(value)
                setLoading(false)
            }
        },
        [yearId, id, requireAcceptance]
    )

    const handleDisable = useCallback(async () => {
        const result = await showDeleteCancel(
            `Disabling this connection will prevent "${name}" from accessing this holding's data; are you sure?`,
            `Disable connecton to partner?`,
            "Disable"
        )
        if (result) {
            setEnabled(false)
        }
    }, [setEnabled])

    const handleEnable = useCallback(() => {
        setEnabled(true)
    }, [setEnabled])

    const handleCroppingsChangeClick = useCallback(async () => {
        const result = await showManagePartnerCroppingsDialog(shell, {
            initialEnabledCroppingIds: croppingIds,
            partnerName: name,
        })
        if (result) {
            onChangeEnabledCroppingIds(result.enabledCroppingIds)
        }
    }, [croppingIds, name, onChangeEnabledCroppingIds, shell])

    return (
        <Stack component="section" direction="row" alignItems="flex-start" gap={2}>
            <Stack flexShrink={0}>
                <Avatar variant="rounded" src={maybeGuessIcon(partner)}>
                    {name[0]}
                </Avatar>
            </Stack>
            <Stack gap={1} flexGrow={1} sx={{ minWidth: 0 }}>
                <Stack direction="row" gap={2} flexGrow={1} alignItems="center" justifyContent="space-between">
                    <Stack direction="row" gap={1} alignItems="center" sx={{ minWidth: 0 }}>
                        <Typography
                            component="h4"
                            sx={{
                                fontSize: "16px",
                                fontWeight: 600,
                                fontFamily: `"Open Sans", "Helvetica Neue", Helvetica, Arial, sans-serif`,
                                textOverflow: "ellipsis",
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {name}
                        </Typography>
                    </Stack>
                    {enabled ? (
                        <Stack direction="row" alignItems="center" gap={1}>
                            <Button
                                variant="link"
                                onClick={handleCroppingsChangeClick}
                                iconClassName="fas fa-seedling"
                                endElement={<ButtonBadge value={croppingIds.length} />}
                                disabled={disabled || isLoading}
                            >
                                Croppings
                            </Button>
                            <DangerButton onClick={handleDisable} disabled={disabled || isLoading}>
                                Disable
                            </DangerButton>
                        </Stack>
                    ) : (
                        <SaveButton onClick={handleEnable} disabled={disabled || isLoading}>
                            Enable
                        </SaveButton>
                    )}
                </Stack>
                {description ? <p>{description}</p> : null}
            </Stack>
        </Stack>
    )
}

function useTermsAndConditionsDialog() {
    return useCallback(
        async ({ ...props }: Omit<TermsAndConditionsDialogProps, "close">) =>
            showDialog({
                content: TermsAndConditionsDialog,
                contentProps: { ...props },
            }),
        [showDialog]
    )
}

/**
 * Temporary method to get images for names of partners, since names are configured in the admin panel, and images are currently static.
 */
function maybeGuessIcon(partner: PartnerDto) {
    if (partner.name.match(/(?:omnia)/gim)?.length) {
        return "/images/brand/omnia/icon-76.png"
    }
    if (partner.name.match(/(?:Mercian)/gim)?.length || partner.name.match(/(?:LiveTrace)/gim)?.length) {
        return "/images/apps/partners/mercian-livetrace-icon.png"
    }
    return undefined
}
